import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const CommunityGuidelinesPage: React.FC = () => {
  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>Community Guidelines | DINK Social</title>
        <meta name="description" content="Read the DINK Social community guidelines. Learn about our standards for respectful interaction in the childfree couples community." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Community Guidelines | DINK Social" />
        <meta property="og:description" content="Read the DINK Social community guidelines for respectful interaction." />
        <meta property="og:url" content="https://dinksocial.com/community-guidelines" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Community Guidelines | DINK Social" />
        <meta name="twitter:description" content="Read the DINK Social community guidelines." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/community-guidelines" />
      </Helmet>

      <Nav />

      {/* ─── HEADING ───────────────────────────────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Community guidelines
          </h1>
          <p
            className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px]"
            style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
          >
            Standards for a respectful, welcoming community
          </p>
        </div>
      </section>

      {/* ─── BODY ──────────────────────────────────────────── */}
      <section className="pb-20 md:pb-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60 mb-12">
            Effective October 12, 2025
          </p>

          <div className="prose prose-lg max-w-none
            prose-headings:font-display prose-headings:font-medium prose-headings:text-ink prose-headings:tracking-tight
            prose-h2:text-[26px] md:prose-h2:text-[32px] prose-h2:mt-12 prose-h2:mb-5 prose-h2:leading-tight prose-h2:normal-case
            prose-h3:text-[22px] md:prose-h3:text-[26px] prose-h3:mt-10 prose-h3:mb-4 prose-h3:leading-tight
            prose-h4:text-[18px] md:prose-h4:text-[20px] prose-h4:mt-8 prose-h4:mb-3 prose-h4:leading-tight
            prose-p:text-ink/75 prose-p:text-[16px] md:prose-p:text-[17px] prose-p:leading-relaxed
            prose-a:text-primary prose-a:no-underline prose-a:font-medium hover:prose-a:text-primary-dark hover:prose-a:underline
            prose-strong:text-ink prose-strong:font-semibold
            prose-ul:text-ink/75 prose-li:text-[16px] md:prose-li:text-[17px] prose-li:leading-relaxed
            prose-li:marker:text-primary/60
            prose-hr:border-ink/10 prose-hr:my-12">

            {/* Welcome */}
            <h2>Welcome</h2>
            <p>
              DINK Social is a community built on connection, kindness, and respect. To maintain a welcoming environment, we draw clear boundaries around behavior that does and does not belong here. These rules apply across all official community spaces, including public forums, private messages related to DINK Social activity, and events hosted by DINK Social. By participating in DINK Social you agree to uphold these standards, as well as our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
            </p>

            {/* Who we are */}
            <h2>Who we are</h2>
            <p><strong>We are respectful.</strong> We treat one another with kindness, courtesy and humility.</p>
            <p><strong>We are adults.</strong> While embracing the D.I.N.K. (Dual Income No Kids) lifestyle can certainly keep you feeling young and having fun, this place is for adults only. You and your significant other must be 18 years of age or older.</p>
            <p><strong>We are in a relationship.</strong> This community exists because childfree couples, whether married, dating, or lifelong partners, deserve a space of their own. We recognize that most online spaces aren't designed with committed couples in mind, especially those exploring nontraditional paths.</p>
            <p><strong>We are childfree.</strong> We love naps, quiet mornings, spontaneous getaways, and drinks that don't come in sippy cups. While kids are cool and we respect parents and their choices, this is a space where being childfree is the norm, not the exception. DINK Social is for couples who are childfree by choice or by life circumstance. It's not a space for couples who are simply postponing children. If your plan includes having kids someday, this likely isn't the right community for you.</p>
            <p><strong>We are inclusive.</strong> We welcome diversity in all its forms, including committed couples of any sexual orientation or gender, and speak in ways that make everyone feel seen, safe, and respected.</p>
            <p><strong>We are mindful.</strong> We protect each other's privacy and uphold trust in every interaction. We are considerate of how our words and actions affect others. We believe in clear, enthusiastic consent — online and off. Always respect personal and relational boundaries.</p>
            <p><strong>We are couple-minded.</strong> While our MVP allows either partner to post, we encourage communication and consensus. Every post represents both of you.</p>
            <p><strong>We are accountable.</strong> We look out for one another and take action when something feels off, because creating a safe space is everyone's job.</p>

            {/* Who we are not */}
            <h2>Who we are not</h2>
            <p>To keep this space respectful, intentional, and safe, we do not allow the following:</p>
            <p><strong>Hate or discrimination.</strong> We reject racism, sexism, homophobia, transphobia, body shaming, ableism, and all forms of exclusion or bigotry.</p>
            <p><strong>Harassment or abuse.</strong> No bullying, threats, intimidation, or personal attacks — whether public or private.</p>
            <p><strong>Shaming others' choices or lifestyles.</strong> We do not tolerate bashing of parents, non-parents, monogamous or non-monogamous individuals. Judgment has no place here.</p>
            <p><strong>Dating app behavior.</strong> This is not a hookup or swinger platform. No unsolicited flirting, propositions, or objectification. Respect personal boundaries at all times.</p>
            <p><strong>Inappropriate or explicit content.</strong> Sexually graphic images, violent material, and NSFW content are strictly prohibited.</p>
            <p><strong>Spamming and self-promotion.</strong> Don't use this space to promote products, events, or accounts unrelated to the community's purpose.</p>
            <p><strong>Privacy violations.</strong> Sharing others' personal details (including screenshots, names, or private messages) without consent is not acceptable.</p>
            <p><strong>Evading moderation.</strong> Creating alternate accounts or dodging bans undermines trust and will result in removal.</p>

            {/* How we post */}
            <h2>How we post</h2>
            <p><strong>We post as couples.</strong> You represent your couple when posting. While our MVP doesn't require technical approval from both partners, we trust couples to communicate before posting on behalf of both.</p>
            <p><strong>We show real perspectives.</strong> When partners disagree, we encourage honest "Split Decision" posts showing both viewpoints.</p>
            <p><strong>We consider both partners.</strong> Even without formal approval requirements, think "would my partner be okay with this?" before posting.</p>

            {/* Reporting misconduct */}
            <h2>Reporting misconduct</h2>
            <p>If you see or experience behavior that violates these guidelines:</p>
            <ul>
              <li>Use the platform's reporting tools (coming soon)</li>
              <li>Or reach out at <a href="mailto:reporting@dinksocial.com">reporting@dinksocial.com</a></li>
            </ul>

            {/* Enforcement */}
            <h2>Enforcement</h2>
            <p>If guidelines are violated, action may be taken including:</p>
            <ul>
              <li>Issuing warnings (public or private)</li>
              <li>Temporarily muting or suspending members</li>
              <li>Removing harmful content</li>
              <li>Banning repeat or severe offenders</li>
            </ul>

            {/* Your couple data */}
            <h2>Your couple data</h2>
            <ul>
              <li>Both partners have equal access to your couple profile</li>
              <li>Either partner can request account deletion</li>
              <li>We never share individual vs. couple decisions with third parties</li>
              <li>Your relationship dynamics stay private</li>
            </ul>

            <p>
              Enforcement decisions are guided by these guidelines, our <Link to="/terms">Terms of Service</Link>, and at the discretion of our moderation team.
            </p>

            <p>
              We're all responsible for maintaining a kind and inclusive community. Thank you for making this a fun and meaningful space to meet new friends.
            </p>

            <p className="font-playfair italic">
              — Team DINK Social
            </p>

            <hr />

            {/* How to use DINK Social */}
            <h2>How to use DINK Social</h2>

            <h3>Getting started</h3>

            <h4>Creating your account</h4>
            <ul>
              <li>One partner signs up with your email and creates a password</li>
              <li>Verify your email address before signing in</li>
              <li>Complete your profile with basic info (name, date of birth, occupation)</li>
              <li>Set up your couple profile (couple name, location, interests, couple types)</li>
              <li>You're immediately discoverable once you complete setup</li>
            </ul>

            <h4>Inviting your partner</h4>
            <ul>
              <li>After completing your profile, you can invite your partner via email</li>
              <li>Your partner will receive an invitation link to join</li>
              <li>Optional: you can skip this step and invite them later</li>
              <li>Both partners verified = "Verified Couple" badge (but not required to use the platform)</li>
            </ul>

            <h3>Using the Lounge — global and local</h3>

            <h4>Creating posts</h4>
            <ul>
              <li>Click the "Create Post" button on the forum page</li>
              <li>Write your message (ask questions, share stories, start discussions)</li>
              <li>Posts are visible to the entire DINK Social community if posted on the Global lounge and only visible to those within 50 miles on the Local lounge</li>
            </ul>

            <h4>Engaging with posts</h4>
            <ul>
              <li>Click on any post to view the full conversation</li>
              <li>Reply to posts to join the discussion</li>
              <li>Refresh the feed to see new posts</li>
            </ul>

            <h4>Navigating replies</h4>
            <ul>
              <li>Click "Reply" on any post to see the full thread</li>
              <li>View the original post author's profile by clicking their couple name</li>
              <li>Navigate back to the main feed anytime</li>
            </ul>

            <h3>Profile features</h3>

            <h4>Your couple profile</h4>
            <ul>
              <li>View and edit your couple name, bio, and location</li>
              <li>Add or update photos</li>
              <li>Select interests that describe you as a couple</li>
              <li>Choose couple types (Global Adventurers, Culture Curators, etc.)</li>
              <li>Update individual partner information</li>
            </ul>

            <h4>Viewing other profiles</h4>
            <ul>
              <li>Click on couple names in posts to view their profiles</li>
              <li>See their interests, couple types, and location</li>
              <li>All active couples are discoverable in the community</li>
            </ul>

            <h3>Tips for the best experience</h3>
            <ul>
              <li>Keep your profile updated with current interests</li>
              <li>Be authentic in your posts and replies</li>
              <li>Refresh the forum page to see the latest content</li>
              <li>Use the navigation to switch between Forum, Profile, and other sections</li>
            </ul>
          </div>

          {/* Mobile-first callout */}
          <div className="mt-12 border-l-2 border-primary pl-5 py-1">
            <p className="font-display font-medium text-ink text-[16px] md:text-[17px] mb-1">
              Best on mobile
            </p>
            <p className="text-[15px] md:text-[16px] text-ink/70" style={{ lineHeight: 1.55 }}>
              DINK Social is designed for mobile. Add us to your home screen for the best experience.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommunityGuidelinesPage;
